import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import LoadingModal from './components/modals/LoadingModal';
import Pagination10 from './components/Pagination10';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import ConfirmModal from './components/modals/ConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import PageCard from './components/PageCard';
import usePagination10 from './components/hooks/usePagination10';
import FavorecidoModal from './components/modals/FavorecidoModal';
import usePermissions from './components/hooks/usePermissions';

function Favorecidos() {
  // Verificação de permissões
  const { podeAbrir, podeCriar, podeEditar, podeExcluir, podeExportar, carregando: carregandoPermissoes } = usePermissions('favorecidos');
  
  const [favorecidos, setFavorecidos] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [atualizar, setAtualizar] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showFavorecidoModal, setShowFavorecidoModal] = useState(false);
  const [favorecidoForModal, setFavorecidoForModal] = useState(null);
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const [isLoading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();

  useEffect(() => {
    if (!token) {
      navigate('/');
    }
    
    // Não carregar dados se não pode abrir o módulo
    if (!carregandoPermissoes && !podeAbrir) {
      return;
    }
    
    setLoading(true);
    axios
      .get(`${API_BASE_URL.FAVORECIDO}/all`, {
        params: { cnpjAssociacao: cnpj },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => {
        setFavorecidos(result.data);
      })
      .catch(error => {
        console.error('Erro ao obter favorecidos:', error);
        showDefaultModal('error', 'Erro ao carregar favorecidos');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [atualizar, token, cnpj, navigate, podeAbrir, carregandoPermissoes]);

  function handleDelete(favorecido) {
    showConfirmModal('delete', () => handleConfirmDelete(favorecido));
  }

  function handleConfirmDelete(favorecido) {
    console.log('Excluindo favorecido:', favorecido);
    axios
      .get(`${API_BASE_URL.DESPESA}/exists_favorecido`, {
        params: {
          cnpjAssociacao: favorecido.cnpjAssociacao,
          cnpjFavorecido: favorecido.cpfOrCnpj,
        },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => {
        if (result.data.exists) {
          showDefaultModal(
            'error',
            'Este favorecido possui vínculos com pagamentos e não pode ser excluído.'
          );
        } else {
          axios
            .delete(`${API_BASE_URL.FAVORECIDO}/delete`, {
              params: {
                cnpjAssociacao: favorecido.cnpjAssociacao,
                cpfOrCnpj: favorecido.cpfOrCnpj,
              },
              headers: { Authorization: `Bearer ${token}` },
            })
            .then(() => {
              setAtualizar(prev => !prev);
              showDefaultModal('success', 'Favorecido excluído com sucesso!');
            })
            .catch(error => {
              console.error('Erro ao excluir favorecido:', error);
              showDefaultModal('error', 'Erro ao excluir o favorecido');
            });
        }
      })
      .catch(error => {
        console.error('Erro ao verificar vínculos:', error);
        showDefaultModal('error', 'Erro ao verificar vínculos do favorecido');
      });
  }

  const normalizeString = str => {
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase();
  };

  const favorecidosFiltrados = Array.isArray(favorecidos)
    ? favorecidos
        .filter(f => {
          const normalizedBusca = normalizeString(filtro);
          return (
            normalizeString(f.cpfOrCnpj).includes(normalizedBusca) ||
            normalizeString(f.nome).includes(normalizedBusca) ||
            (f.logrCidade &&
              normalizeString(f.logrCidade).includes(normalizedBusca))
          );
        })
        .sort((a, b) => {
          const nomeA = normalizeString(a.nome || '');
          const nomeB = normalizeString(b.nome || '');
          return nomeA.localeCompare(nomeB);
        })
    : [];

  const totalItems = favorecidosFiltrados.length;
  const { setPagination } = usePagination10(totalItems);
  const selectedFavorecidos = favorecidosFiltrados.slice(
    setPagination.startIndex,
    setPagination.startIndex + setPagination.rowsPerPage
  );

  function toggleSelect(favorecido) {
    const key = `${favorecido.cnpjAssociacao}_${favorecido.cpfOrCnpj}`;
    const selectedKey = selected ? `${selected.cnpjAssociacao}_${selected.cpfOrCnpj}` : null;
    setSelected(selectedKey === key ? null : favorecido);
  }

  function handleOpenModalNew() {
    setFavorecidoForModal(null);
    setShowFavorecidoModal(true);
  }

  function handleOpenModalEdit() {
    if (!selected) return;
    setFavorecidoForModal(selected);
    setShowFavorecidoModal(true);
  }

  function handleCloseFavorecidoModal() {
    setShowFavorecidoModal(false);
    setFavorecidoForModal(null);
  }

  function handleFavorecidoModalSubmit() {
    setAtualizar(prev => !prev);
    handleCloseFavorecidoModal();
  }

  // Verificar se ainda está carregando permissões
  if (carregandoPermissoes || isLoading) {
    return <LoadingModal loading={true} />;
  }

  return (
    <PageCard
      title="Favorecidos Cadastrados"
      icon="bi-people"
      scrollable={false}
      footer={favorecidosFiltrados.length > 0 ? <Pagination10 {...setPagination} /> : null}
    >
      {!podeAbrir ? (
        <div className="alert alert-warning m-3">
          <i className="bi bi-exclamation-triangle me-2"></i>
            <strong>Acesso Negado</strong>
            <p className="mb-0 mt-2">
              Você não tem permissão para acessar o módulo de Favorecidos.
            </p>
            <p className="mb-0 mt-2">
              <small>Entre em contato com o administrador do sistema.</small>
            </p>
            <button 
              className="btn btn-sm btn-outline-secondary mt-3"
              onClick={() => navigate('/home')}
            >
              <i className="bi bi-arrow-left me-1"></i>
              Voltar
            </button>
          </div>
        ) : (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <div className="d-flex flex-wrap gap-1 mb-2 p-2 bg-light rounded border align-items-center">
            <div className="d-flex gap-2">
              <button
                onClick={handleOpenModalNew}
                className="btn btn-success btn-sm d-inline-flex align-items-center"
                disabled={!podeCriar}
                title={!podeCriar ? 'Você não tem permissão para criar favorecidos' : 'Novo favorecido'}
              >
                <i className="bi bi-plus-lg me-1" />
                Novo
              </button>
              <button
                onClick={handleOpenModalEdit}
                disabled={!podeEditar || !selected}
                className="btn btn-warning btn-sm d-inline-flex align-items-center"
                title={!podeEditar ? 'Você não tem permissão para editar' : !selected ? 'Selecione um favorecido' : 'Editar favorecido selecionado'}
              >
                <i className="bi bi-pencil me-1" />
                Editar
              </button>
              <button
                onClick={() => {if (selected) handleDelete(selected);}}
                disabled={!podeExcluir || !selected}
                className="btn btn-danger btn-sm d-inline-flex align-items-center"
                title={!podeExcluir ? 'Você não tem permissão para excluir' : !selected ? 'Selecione um favorecido' : 'Excluir favorecido selecionado'}
              >
                <i className="bi bi-trash me-1" />
                Excluir
              </button>
            </div>

            <div className="ms-auto d-flex align-items-center">
              <div className="input-group" style={{ maxWidth: 300, height: 31 }}>
                <span className="input-group-text bg-white border-end-0 px-2" style={{ height: 31 }}>
                  <i className="bi bi-search text-muted" style={{ fontSize: 13 }} />
                </span>
                <input
                  type="text"
                  className="form-control form-control-sm border-start-0"
                  style={{ textTransform: 'uppercase', height: 31, fontSize: 13 }}
                  placeholder="Buscar por CNPJ, Nome ou Cidade"
                  value={filtro}
                  onChange={e => setFiltro(e.target.value)}
                />
                {filtro && (
                  <button
                    className="btn btn-outline-secondary btn-sm border-start-0 d-flex align-items-center px-2"
                    style={{ height: 31 }}
                    onClick={() => setFiltro('')}
                    title="Limpar pesquisa"
                    tabIndex={-1}
                  >
                    <i className="bi bi-x-lg" style={{ fontSize: 11 }} />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div
            className="table-responsive-sm shadow-sm mb-0 small"
            style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
          >
            <table className="table table-hover table-striped table-sm">
              <thead className="table-secondary sticky-top bg-light">
                <tr>
                  <th scope="col" className="align-middle">
                    CNPJ/CPF
                  </th>
                  <th scope="col" className="align-middle">
                    Nome/Razão Social
                  </th>
                  <th scope="col" className="align-middle d-none d-md-table-cell">
                    Endereço
                  </th>
                </tr>
              </thead>
              <tbody>
                {!isLoading &&
                  selectedFavorecidos.map(f => {
                    const key = `${f.cnpjAssociacao}_${f.cpfOrCnpj}`;
                    const selectedKey = selected ? `${selected.cnpjAssociacao}_${selected.cpfOrCnpj}` : null;
                    const isSelected = selectedKey === key;
                    return (
                      <tr
                        key={key}
                        onClick={() => toggleSelect(f)}
                        className={`${isSelected ? 'table-success' : ''}`}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>{f.cpfOrCnpj}</td>
                        <td>{f.nome}</td>
                        <td className="d-none d-md-table-cell">
                          {f.logrNome && `${f.logrNome}, ${f.logrNum}`}{' '}
                          {f.logrCompl && ` - ${f.logrCompl}`}
                          <br />
                          {f.logrBairro && `${f.logrBairro} - `} {f.logrCidade}/
                          {f.logrUf}
                        </td>
                      </tr>
                    );
                  })}
                {!isLoading && favorecidosFiltrados.length === 0 && (
                  <tr>
                    <td colSpan="3" className="text-center">
                      Nenhum favorecido encontrado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <FavorecidoModal
        show={showFavorecidoModal}
        onClose={handleCloseFavorecidoModal}
        onSubmit={handleFavorecidoModalSubmit}
        favorecido={favorecidoForModal}
        isEditing={favorecidoForModal !== null}
        title={favorecidoForModal ? 'EDITAR FAVORECIDO' : 'CADASTRO DE FAVORECIDO'}
      />
      <DefaultModal {...setDefaultModal} />
      <ConfirmModal {...setConfirmModal} />
    </PageCard>
  );
}

export default Favorecidos;
